// src/pages/StudentLessons.js
import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchAllLessons } from '../../../services/studentService';
import './StudentLessons.css';

function StudentLessons() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, upcoming, past

  useEffect(() => {
    loadLessons();
  }, []);

  const loadLessons = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchAllLessons();
      setLessons(data);
    } catch (error) {
      setError('Error loading lessons: ' + (error.response?.data?.msg || error.message));
      console.error('Dersler yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLessons = () => {
    const now = new Date();
    switch (filter) {
      case 'upcoming':
        return lessons.filter(lesson => new Date(lesson.scheduledAt) > now);
      case 'past':
        return lessons.filter(lesson => new Date(lesson.scheduledAt) <= now);
      default:
        return lessons;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLessonStatus = (lessonDate) => {
    const now = new Date();
    const lessonTime = new Date(lessonDate);
    
    if (lessonTime < now) return { status: 'past', text: 'Completed', class: 'past' };
    
    const diffMs = lessonTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    if (diffHours < 1) {
      return { status: 'soon', text: 'Starting Soon', class: 'soon' };
    }
    if (diffHours < 24) {
      return { status: 'today', text: 'Today', class: 'today' };
    }
    if (diffHours < 48) {
      return { status: 'tomorrow', text: 'Tomorrow', class: 'tomorrow' };
    }
    
    return { status: 'upcoming', text: 'Upcoming', class: 'upcoming' };
  };

  const addToCalendar = (lesson) => {
    // Create Google Calendar link
    const startTime = new Date(lesson.scheduledAt);
    const endTime = new Date(startTime.getTime() + lesson.durationMinutes * 60000);
    
    const formatGoogleCalendarDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };
    
    const start = formatGoogleCalendarDate(startTime);
    const end = formatGoogleCalendarDate(endTime);
    
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(lesson.title)}&dates=${start}/${end}&details=${encodeURIComponent(lesson.course?.title || '')}&location=${encodeURIComponent(lesson.zoomLink || '')}`;
    
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="student-lessons-loading">
        <div className="loading-spinner">⏰</div>
        <p>Loading lesson schedule...</p>
      </div>
    );
  }

  const filteredLessons = filterLessons();

  return (
    <div className="student-lessons">
      {/* Header */}
      <div className="lessons-header">
        <div className="header-content">
          <h1>⏰ My Lesson Schedule</h1>
          <p>All lessons you will attend and have attended</p>
        </div>
        <button onClick={loadLessons} className="refresh-btn" disabled={loading}>
          <span className="refresh-icon">🔄</span>
          Refresh
        </button>
      </div>

      {error && (
        <div className="lessons-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All Lessons ({lessons.length})
          </button>
          <button 
            className={filter === 'upcoming' ? 'active' : ''}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming Lessons ({lessons.filter(l => new Date(l.scheduledAt) > new Date()).length})
          </button>
          <button 
            className={filter === 'past' ? 'active' : ''}
            onClick={() => setFilter('past')}
          >
            Past Lessons ({lessons.filter(l => new Date(l.scheduledAt) <= new Date()).length})
          </button>
        </div>
      </div>

      {/* Lesson List */}
      {filteredLessons.length === 0 ? (
        <div className="empty-lessons">
          <div className="empty-icon">📅</div>
          <h3>No lessons found</h3>
          <p>No lessons match your selected filter</p>
        </div>
      ) : (
        <div className="lessons-list">
          {filteredLessons.map(lesson => {
            const status = getLessonStatus(lesson.scheduledAt);
            
            return (
              <div key={lesson._id} className={`lesson-item ${status.class}`}>
                <div className="lesson-main">
                  <div className="lesson-info">
                    <h3>{lesson.title}</h3>
                    <p className="course-name">{lesson.course?.title}</p>
                    <p className="lesson-time">{formatDate(lesson.scheduledAt)}</p>
                    <p className="lesson-duration">{lesson.durationMinutes} minutes</p>
                  </div>
                  
                  <div className="lesson-status">
                    <span className={`status-badge ${status.class}`}>
                      {status.text}
                    </span>
                    {status.status !== 'past' && (
                      <span className="time-until">
                        {new Date(lesson.scheduledAt).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="lesson-actions">
                  {lesson.zoomLink && status.status !== 'past' && (
                    <a 
                      href={lesson.zoomLink} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="join-btn"
                    >
                      🎥 Join Zoom
                    </a>
                  )}
                  {status.status !== 'past' && (
                    <button 
                      className="calendar-btn"
                      onClick={() => addToCalendar(lesson)}
                    >
                      📅 Add to Calendar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StudentLessons;